import { Box, Drawer, Tooltip, Typography, IconButton } from '@mui/material';
import { importCsv } from 'apis/business/agent/worksheet/api';
import { useCsvAppend, useGetColumns } from 'apis/business/agent/worksheet/hook';
import UploadFile from 'components/Common/UploadFile';
import Button from 'components/Common/Button';
import { useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Select from 'components/Common/Select';
import { useForm } from 'react-hook-form';
import Switch from 'components/Common/Switch';

export default function ImportCsvForm() {
  const navigate = useNavigate();
  const { locationId, agentId, worksheetId } = useParams<{
    locationId: string;
    agentId: string;
    worksheetId: string;
  }>();

  const [selectedFiles, setSelectedFiles] = useState<null | any[]>(null);
  const [csvColumns, setCsvColumns] = useState<string[] | null>(null);
  const [view, setView] = useState<'upload' | 'columns'>('upload');
  const [isImporting, setIsImporting] = useState(false);

  const { data: columnsData } = useGetColumns({ include: 'cells,workflow', filter: { data_type_eq: '0' } });
  const { mutateAsync: appendCsv, isPending: isAppending } = useCsvAppend();

  const handleFileChange = async (files: File[]) => {
    const data = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      data.push(file);
    }
    setSelectedFiles(data);

    const reader = new FileReader();
    reader.readAsText(files[0]);
  };

  const { control, watch, handleSubmit, setValue } = useForm();

  const onSubmit = async () => {
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFiles?.[0]);
      const response = await importCsv(locationId!, agentId!, worksheetId!, formData);
      setCsvColumns(response?.csv_columns);
      if (response?.csv_columns?.length) {
        setView('columns');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleAppendCsv = (data: any) => {
    const updatedData = data?.worksheet?.map((item: any, index: number) => {
      const csvColumnValue = csvColumns?.[index] ?? null;
      if (item.skip_import) {
        return { ...item, column_id: null, csv_column: csvColumnValue };
      } else {
        return {
          ...item,
          column_id: item.column_id === undefined ? null : item.column_id,
          csv_column: csvColumnValue
        };
      }
    });
    const payload = {
      worksheet: updatedData
    };
    appendCsv(payload);
  };
  const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: number | string | null }>({});

  const handleSelectChange = (index: number, value: number | string | null) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [index]: value
    }));
    if (value !== 'create_new') {
      setValue(`worksheet[${index}].column_id`, value);
    }
  };
  // Derive the list of currently selected column IDs
  const selectedColumnIds = Object.values(selectedOptions).filter((id) => id);

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>{view === 'upload' ? 'Import CSV' : 'Map CSV to Grid'}</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={view === 'upload' ? onSubmit : handleSubmit(handleAppendCsv)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          {view === 'upload' && (
            <Box>
              <UploadFile
                allowedFileTypes={['xlsx', 'csv']}
                selectedFiles={selectedFiles}
                handleChange={handleFileChange}
              />
            </Box>
          )}
          {view === 'columns' && (
            <Box border='2px solid #F3F3F3' borderRadius='10px' padding='20px'>
              <>
                <Grid container mt={1} spacing={3} alignItems='center'>
                  <Grid size={3}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      CSV COLUMN
                    </Typography>
                  </Grid>
                  <Grid size={3}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      Do Not Import
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography fontSize='14px' fontWeight='bold'>
                      GRID COLUMN
                    </Typography>
                  </Grid>
                </Grid>
                {csvColumns?.map((item, index) => (
                  <Grid container mt={2} spacing={3} key={index} alignItems='center'>
                    <Grid size={3}>
                      <Typography>{item}</Typography>
                    </Grid>
                    <Grid size={3} textAlign='center'>
                      <Tooltip title='Do Not Import'>
                        <Switch
                          name={`worksheet[${index}].skip_import`}
                          control={control}
                          size='small'
                          onToggle={(isChecked) => {
                            if (isChecked) {
                              setSelectedOptions((prev) => ({
                                ...prev,
                                [index]: null
                              }));
                            }
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid size={6} height={40}>
                      {!watch(`worksheet[${index}].skip_import`) && (
                        <Select
                          name={`worksheet[${index}].column_id`}
                          control={control}
                          label='column'
                          size='small'
                          options={[
                            { label: 'Create New Column', value: 'create_new' },
                            ...(columnsData?.data
                              ?.filter((col) => col.data_type !== 'app_execution')
                              .map((col) => ({
                                label: col.name,
                                value: col.id,
                                disabled: selectedColumnIds.includes(col.id)
                              })) ?? [])
                          ]}
                          onChange={(e) => {
                            const selectedValue = e.target.value === 'create_new' ? null : Number(e.target.value);
                            handleSelectChange(index, selectedValue);
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}
              </>
            </Box>
          )}
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: 2,
            flexShrink: 0
          }}
        >
          {view === 'upload' && (
            <Button type='submit' variant='contained' disabled={!selectedFiles?.length} isPending={isImporting}>
              Continue
            </Button>
          )}
          {view === 'columns' && (
            <>
              <Button type='button' variant='outlined' onClick={() => setView('upload')}>
                Back
              </Button>
              <Button type='submit' isPending={isAppending}>
                Submit
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
